import { useQuery } from "@tanstack/react-query";
import { employeesAPI } from "../services/api";
import { useMemo } from "react";

interface UpcomingAnniversary {
  employee: any;
  date: Date;
  daysUntil: number;
  years: number;
}

export const useUpcomingAnniversaries = (
  daysAhead: number = 30,
  enabled: boolean = true,
) => {
  const { data: employees = [], isLoading } = useQuery({
    queryKey: ["employees"],
    queryFn: employeesAPI.getAll,
    enabled,
  });

  const upcomingAnniversaries = useMemo(() => {
    if (!employees || employees.length === 0) return [];

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const endDate = new Date(today);
    endDate.setDate(endDate.getDate() + daysAhead);

    const anniversaries: UpcomingAnniversary[] = [];

    employees.forEach((employee: any) => {
      if (!employee.hireDate || !employee.isActive) return;

      const hireDate = new Date(employee.hireDate);
      const currentYear = today.getFullYear();
      const hireYear = hireDate.getFullYear();

      // Calculate work anniversary for this year
      const thisYearAnniversary = new Date(
        currentYear,
        hireDate.getMonth(),
        hireDate.getDate(),
      );

      // If anniversary already passed this year, use next year
      const nextAnniversary =
        thisYearAnniversary < today
          ? new Date(currentYear + 1, hireDate.getMonth(), hireDate.getDate())
          : thisYearAnniversary;

      // Check if anniversary is within the specified days ahead
      if (nextAnniversary >= today && nextAnniversary <= endDate) {
        const daysUntil = Math.ceil(
          (nextAnniversary.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
        );
        const years = nextAnniversary.getFullYear() - hireYear;

        anniversaries.push({
          employee,
          date: nextAnniversary,
          daysUntil,
          years,
        });
      }
    });

    // Sort by date (soonest first)
    return anniversaries.sort((a, b) => a.date.getTime() - b.date.getTime());
  }, [employees, daysAhead]);

  return {
    upcomingAnniversaries,
    isLoading,
  };
};
